import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux";
import Router from "./Router";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// components
import ScrollToTop from "./components/scroll-to-top";
import SimpleLoader from "./components/loader";
import ToastMsg from "./components/toast";
import { setLinkUTM } from "./redux/user/userActions"
import { showToast, setLoggedInPath } from "./redux/common/commonActions";

const structuredJSON = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Acadlance",
  "url": "https://www.acadlance.com/",
  "logo": "https://acadlance.com/logo.webp",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91 73032 47873",
    "contactType": "customer service",
    "areaServed": "IN"
  },
  "sameAs": [
    "https://www.linkedin.com/company/acadlance",
    "https://www.instagram.com/acadlance/"
  ]
})

const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.userState?.isLoggedIn);
  const [currLoc, setCurrLoc] = useState(window.location.href)
  const [pageTitle, setPageTitle] = useState(document?.title)
  const [pageDescription, setPageDescription] = useState(
    document.querySelector('meta[name="description"]')?.getAttribute('content')
  );

  const getCurrentDocVals = () => {
    const currentTitle = document.title;
    const currentDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');

    if (currentTitle !== pageTitle) setPageTitle(currentTitle);
    if (currentDescription !== pageDescription) setPageDescription(currentDescription);
  }

  useEffect(() => {
    getCurrentDocVals()
    const interval = setInterval(() => {
      getCurrentDocVals()
    }, 1000);

    // Clear the interval after 3 seconds
    const timeout = setTimeout(() => clearInterval(interval), 3000);

    // Cleanup both interval and timeout on component unmount
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [pageTitle, pageDescription]);

  useEffect(() => {
    const fullPathName = window.location.origin + location?.pathname
    setCurrLoc(fullPathName)
    if (!isLoggedIn && fullPathName?.includes("academic-certification")) {
      dispatch(setLoggedInPath({
        path: "/client-expert/academic-certification",
        userType: 'Expert',
      }))

      dispatch(showToast({ show: true, msg: "Please log in to your Acadlance account to check the Certified Test details!" }))
      setTimeout(() => {
        dispatch(showToast({ show: false, msg: "" }))
      }, 10 * 1000)
    }

    if (location?.search && location?.search?.toLocaleLowerCase()?.includes('utm')) {
      dispatch(setLinkUTM(location?.search))
    }
  }, [location]);

  const loadGTagScript = () => {
    if (!document.querySelector('script[src="https://www.googletagmanager.com/gtag/js?id=G-2MG23LPX6C"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-2MG23LPX6C';
      script.async = true;
      document.body.appendChild(script);
    }

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-2MG23LPX6C');

  };

  // Load Clarity script
  const loadClarityScript = () => {
    if (!document.querySelector('script[src="https://www.clarity.ms/tag/m8mvbtqmnx"]')) {
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
        t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', 'm8mvbtqmnx');
    }
  };

  // Load Meta Pixel script
  const loadMetaPixelScript = () => {
    if (!window.fbq) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    window.fbq('init', '1517643342464766');
    window.fbq('track', 'PageView');

    // Add noscript element for Meta Pixel
    if (!document.querySelector('noscript img[src="https://www.facebook.com/tr?id=1517643342464766&ev=PageView&noscript=1"]')) {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1517643342464766&ev=PageView&noscript=1" />`;
      document.body.appendChild(noscript);
    }
  };

  useEffect(() => {
    // Load all scripts after the component mounts
    setPageTitle(document?.title)
    loadGTagScript();
    loadClarityScript();
    loadMetaPixelScript();
  }, [])

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name='dmca-site-verification' content='Y0pCU1prckU1TWwxdHhsQTVsR0ovejFYTHBZUW5rOHQyamVkYnNobXkrMD01' />
        <meta name="google-site-verification" content="9mkIRCCGOkzgrahgLgEkMvT3-HJ5I32Erupx-xophzo" />
        <link rel="canonical" href={currLoc} />
        <meta property="og:title" content="Acadlance - Marketplace for Freelance Academic Writing" />
        <meta property="og:description" content="Acadlance connects students with professional academic writers, creating opportunities for innovative and successful academic projects." />
        <meta property="og:url" content={currLoc} />
        <script className='structured-home-data' type="application/ld+json">{structuredJSON}</script>
      </Helmet>
      <ScrollToTop />
      <Router />
      <SimpleLoader />
      <ToastMsg />
    </>
  );
}

export default App